import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Project: Blog App with React Router
//  Chapter 10 · Projects
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `// app/routes.jsx
export const routes = [
  { path: '/',                  element: <HomePage /> },
  { path: '/blog',              element: <BlogListPage /> },
  { path: '/blog/:slug',        element: <PostDetailPage /> },
  { path: '/category/:cat',     element: <CategoryPage /> },
  { path: '/author/:authorId',  element: <AuthorPage /> },
  { path: '/search',            element: <SearchPage /> },
  { path: '*',                  element: <NotFoundPage /> },
];`;

const CODE_2 = `// pages/PostDetailPage.jsx
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch';

function readingTime(text) {
  const words = text.split(' ').length;
  return Math.ceil(words / 200);
}

export default function PostDetailPage() {
  const { slug }     = useParams();
  const navigate     = useNavigate();
  const { data: post, loading, error } = useFetch(\`/api/posts/\${slug}\`);

  if (loading) return <ArticleSkeleton />;
  if (error)   return <Navigate to="/404" />;

  return (
    <article className="post-detail">
      <nav className="breadcrumb">
        <Link to="/blog">Blog</Link> /
        <Link to={\`/category/\${post.category}\`}>{post.category}</Link> /
        <span>{post.title}</span>
      </nav>

      <header>
        <img src={post.coverImage} alt={post.title} className="cover" />
        <h1>{post.title}</h1>
        <div className="meta">
          <Link to={\`/author/\${post.author.id}\`}>{post.author.name}</Link>
          <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
          <span>☕ {readingTime(post.content)} min read</span>
        </div>
      </header>

      <div className="content" dangerouslySetInnerHTML={{ __html: post.htmlContent }} />

      <footer>
        <div className="tags">
          {post.tags.map(tag => (
            <Link key={tag} to={\`/search?tag=\${tag}\`} className="tag">#{tag}</Link>
          ))}
        </div>
        <button onClick={() => navigate(-1)}>← Back to Blog</button>
      </footer>
    </article>
  );
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function ProjectBlogWithReactRouter() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 10 · Projects</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Project: Blog App with React Router</h1>
        <p style={styles.desc}>Build a fully routed blog application with posts, categories, and search.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── Project Overview ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Project Overview</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "A blog application with:" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Home page with featured and recent posts" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Post listing with category filter and search" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Individual post detail page with reading time" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Category pages" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Author pages" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• 404 page with suggestions" }} />
      </div>

      /* ── Route Structure ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Route Structure</h2>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── PostDetailPage with Params ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>PostDetailPage with Params</h2>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Extension Challenges ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Extension Challenges</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 EXTENSION CHALLENGES</div>
          <div style={styles.qItem}>✦ 1. Add a comment section with local state</div>
          <div style={styles.qItem}>✦ 2. Implement a \'Like\' button per post with localStorage persistence</div>
          <div style={styles.qItem}>✦ 3. Add a reading progress bar at the top of the page</div>
          <div style={styles.qItem}>✦ 4. Add estimated reading time to the post list cards</div>
          <div style={styles.qItem}>✦ 5. Implement infinite scroll on the blog list page</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
